<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import WahaIntegrationsAPI from 'dashboard/api/wahaIntegrations';

const props = defineProps({
  channelIdentifier: {
    type: String,
    required: true,
  },
});

defineOptions({ inheritAttrs: false });

const { t } = useI18n();

const initialLoading = ref(true);
const refreshing = ref(false);
const error = ref(null);
const sessionStatus = ref('');
const sessionMe = ref(null);
const qrCode = ref(null);
const loadingQR = ref(false);
const actionLoading = ref(false);
const showQRModal = ref(false);
const qrCountdown = ref(0);
const lastQRFetch = ref(null);
const authMethod = ref('qr');
const pairingPhoneNumber = ref('');
const pairingCode = ref('');
const pairingError = ref('');
const loadingPairing = ref(false);
const profileData = ref(null);
const profileNameEdit = ref('');
const profileStatusEdit = ref('');
const savingProfile = ref(false);
const timelockData = ref(null);
const cappingData = ref(null);
let pollingInterval = null;
let countdownInterval = null;
const QR_REFRESH_SECONDS = 15;

let stopPolling;
let stopQRAutoRefresh;
let fetchQRCode;
let fetchStatus;
let fetchSessionMe;
let fetchQuotaAlerts;
let fetchProfile;
let startPolling;
let startQRAutoRefresh;

const statusColor = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return 'text-green-600 dark:text-green-400';
    case 'SCAN_QR_CODE':
      return 'text-blue-600 dark:text-blue-400';
    case 'STARTING':
      return 'text-yellow-600 dark:text-yellow-400';
    case 'STOPPED':
      return 'text-slate-600 dark:text-slate-400';
    case 'FAILED':
      return 'text-red-600 dark:text-red-400';
    default:
      return 'text-slate-600 dark:text-slate-400';
  }
});

const statusBgColor = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return 'bg-green-100 dark:bg-green-900/30';
    case 'SCAN_QR_CODE':
      return 'bg-blue-100 dark:bg-blue-900/30';
    case 'STARTING':
      return 'bg-yellow-100 dark:bg-yellow-900/30';
    case 'STOPPED':
      return 'bg-slate-100 dark:bg-slate-700';
    case 'FAILED':
      return 'bg-red-100 dark:bg-red-900/30';
    default:
      return 'bg-slate-100 dark:bg-slate-700';
  }
});

const statusDotColor = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return 'bg-green-600 dark:bg-green-400 animate-pulse';
    case 'SCAN_QR_CODE':
      return 'bg-blue-600 dark:bg-blue-400 animate-pulse';
    case 'STARTING':
      return 'bg-yellow-600 dark:bg-yellow-400 animate-pulse';
    case 'STOPPED':
      return 'bg-slate-600 dark:bg-slate-400';
    case 'FAILED':
      return 'bg-red-600 dark:bg-red-400 animate-pulse';
    default:
      return 'bg-slate-600 dark:bg-slate-400';
  }
});

const sessionStatusText = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_CONNECTED');
    case 'SCAN_QR_CODE':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_SCANNING');
    case 'STARTING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STARTING');
    case 'STOPPED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STOPPED');
    case 'FAILED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_FAILED');
    default:
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_UNKNOWN');
  }
});

const statusDescription = computed(() => {
  switch (sessionStatus.value) {
    case 'WORKING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_CONNECTED_DESC');
    case 'SCAN_QR_CODE':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_SCANNING_DESC');
    case 'STARTING':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STARTING_DESC');
    case 'STOPPED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STOPPED_DESC');
    case 'FAILED':
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_FAILED_DESC');
    default:
      return t('INBOX_MGMT.WAHA_SETTINGS.SESSION_UNKNOWN_DESC');
  }
});

const sessionSubtitle = computed(() => {
  if (sessionStatus.value === 'WORKING' && sessionMe.value?.id) {
    const number = sessionMe.value.id.replace('@c.us', '');
    const about = profileData.value?.status;
    return about ? `${number} · ${about}` : number;
  }
  return statusDescription.value;
});

startPolling = () => {
  stopPolling();
  pollingInterval = setInterval(() => {
    fetchStatus(true);
  }, 5000);
};

stopPolling = () => {
  if (pollingInterval) {
    clearInterval(pollingInterval);
    pollingInterval = null;
  }
};

startQRAutoRefresh = () => {
  stopQRAutoRefresh();
  qrCountdown.value = QR_REFRESH_SECONDS;
  lastQRFetch.value = Date.now();
  countdownInterval = setInterval(() => {
    const elapsed = Math.floor((Date.now() - lastQRFetch.value) / 1000);
    qrCountdown.value = Math.max(0, QR_REFRESH_SECONDS - elapsed);
    if (qrCountdown.value === 0 && !loadingQR.value) {
      fetchQRCode();
    }
  }, 1000);
};

stopQRAutoRefresh = () => {
  if (countdownInterval) {
    clearInterval(countdownInterval);
    countdownInterval = null;
  }
  qrCountdown.value = 0;
};

fetchStatus = async (isRefresh = false) => {
  const timeoutId = setTimeout(() => {
    if (initialLoading.value || refreshing.value) {
      error.value = t('INBOX_MGMT.WAHA_SETTINGS.TIMEOUT_ERROR');
      initialLoading.value = false;
      refreshing.value = false;
    }
  }, 10000);

  try {
    if (isRefresh) {
      refreshing.value = true;
    } else {
      initialLoading.value = true;
    }
    error.value = null;

    const response = await WahaIntegrationsAPI.getSessionStatus(
      props.channelIdentifier
    );
    clearTimeout(timeoutId);

    if (response && response.data) {
      const newStatus = response.data.status || 'UNKNOWN';
      const oldStatus = sessionStatus.value;
      sessionStatus.value = newStatus;

      if (newStatus === 'SCAN_QR_CODE') {
        if (oldStatus !== 'SCAN_QR_CODE') {
          await fetchQRCode();
          startQRAutoRefresh();
        }
        startPolling();
      } else if (newStatus === 'STARTING') {
        qrCode.value = null;
        stopQRAutoRefresh();
        startPolling();
      } else if (newStatus === 'WORKING' && oldStatus === 'SCAN_QR_CODE') {
        qrCode.value = null;
        stopPolling();
        stopQRAutoRefresh();
        fetchSessionMe();
        fetchQuotaAlerts();
        fetchProfile();
      } else if (newStatus === 'WORKING') {
        qrCode.value = null;
        stopPolling();
        stopQRAutoRefresh();
        fetchSessionMe();
        fetchQuotaAlerts();
        fetchProfile();
      } else if (['STOPPED', 'FAILED'].includes(newStatus)) {
        qrCode.value = null;
        sessionMe.value = null;
        profileData.value = null;
        stopPolling();
        stopQRAutoRefresh();
      }
    } else {
      throw new Error('Respuesta inválida del servidor');
    }
  } catch (err) {
    clearTimeout(timeoutId);
    error.value =
      err.response?.data?.error ||
      err.message ||
      t('INBOX_MGMT.WAHA_SETTINGS.CONNECTION_ERROR');
    sessionStatus.value = 'UNKNOWN';
    stopPolling();
  } finally {
    initialLoading.value = false;
    refreshing.value = false;
  }
};

fetchQRCode = async () => {
  try {
    loadingQR.value = true;
    const response = await WahaIntegrationsAPI.getQRCode(
      props.channelIdentifier
    );
    qrCode.value = response.data;
    lastQRFetch.value = Date.now();
    qrCountdown.value = QR_REFRESH_SECONDS;
  } catch (err) {
    error.value =
      err.response?.data?.error ||
      err.message ||
      t('INBOX_MGMT.WAHA_SETTINGS.CONNECTION_ERROR');
  } finally {
    loadingQR.value = false;
  }
};

fetchSessionMe = async () => {
  try {
    const response = await WahaIntegrationsAPI.getSessionMe(
      props.channelIdentifier
    );
    if (response && response.data) {
      sessionMe.value = response.data;
    }
  } catch {
    sessionMe.value = null;
  }
};

const requestPairingCode = async () => {
  if (!pairingPhoneNumber.value) return;
  try {
    loadingPairing.value = true;
    pairingCode.value = '';
    pairingError.value = '';
    const response = await WahaIntegrationsAPI.requestPairingCode(
      props.channelIdentifier,
      pairingPhoneNumber.value
    );
    const result = response?.data;
    const code =
      result?.code ||
      result?.pairingCode ||
      result?.data?.code ||
      result?.data?.pairingCode ||
      (typeof result === 'string' ? result : '');
    if (!code) {
      pairingError.value = t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_EMPTY');
      return;
    }
    pairingCode.value = code;
    startPolling();
  } catch (err) {
    pairingError.value =
      err.response?.data?.error ||
      t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_ERROR');
  } finally {
    loadingPairing.value = false;
  }
};

fetchProfile = async () => {
  try {
    const response = await WahaIntegrationsAPI.getProfile(
      props.channelIdentifier
    );
    if (response?.data) {
      profileData.value = response.data;
      profileNameEdit.value = response.data.name || '';
      profileStatusEdit.value = response.data.status || '';
    }
  } catch {
    profileData.value = null;
  }
};

const saveProfileName = async () => {
  try {
    savingProfile.value = true;
    await WahaIntegrationsAPI.updateProfileName(
      props.channelIdentifier,
      profileNameEdit.value
    );
    await fetchProfile();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error saving profile name';
  } finally {
    savingProfile.value = false;
  }
};

const saveProfileStatus = async () => {
  try {
    savingProfile.value = true;
    await WahaIntegrationsAPI.updateProfileStatus(
      props.channelIdentifier,
      profileStatusEdit.value
    );
    await fetchProfile();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error saving profile status';
  } finally {
    savingProfile.value = false;
  }
};

fetchQuotaAlerts = async () => {
  try {
    const [tlResponse, capResponse] = await Promise.allSettled([
      WahaIntegrationsAPI.getTimelock(props.channelIdentifier),
      WahaIntegrationsAPI.getCapping(props.channelIdentifier),
    ]);
    timelockData.value =
      tlResponse.status === 'fulfilled' ? tlResponse.value?.data : null;
    cappingData.value =
      capResponse.status === 'fulfilled' ? capResponse.value?.data : null;
  } catch {
    timelockData.value = null;
    cappingData.value = null;
  }
};

const refreshStatus = async () => {
  await fetchStatus(true);
};

const startSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.startSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al iniciar la sesión';
  } finally {
    actionLoading.value = false;
  }
};

const stopSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.stopSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al detener la sesión';
  } finally {
    actionLoading.value = false;
  }
};

const restartSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.restartSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al reiniciar la sesión';
  } finally {
    actionLoading.value = false;
  }
};

const logoutSession = async () => {
  try {
    actionLoading.value = true;
    await WahaIntegrationsAPI.logoutSession(props.channelIdentifier);
    await fetchStatus();
  } catch (err) {
    error.value = err.response?.data?.error || 'Error al cerrar la sesión';
  } finally {
    actionLoading.value = false;
  }
};

onMounted(() => {
  fetchStatus();
});

onUnmounted(() => {
  stopPolling();
  stopQRAutoRefresh();
});
</script>

<template>
  <div
    class="border border-slate-200 dark:border-slate-600 rounded-lg p-6 bg-white dark:bg-slate-800"
  >
    <div class="flex items-center justify-between mb-6">
      <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
        {{ $t('INBOX_MGMT.WAHA_SETTINGS.TITLE') }}
      </h3>
      <button
        :disabled="refreshing"
        class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-white dark:bg-slate-600 border border-slate-300 dark:border-slate-500 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-500 disabled:opacity-50 transition-opacity"
        @click="refreshStatus"
      >
        <span v-if="refreshing">{{
          $t('INBOX_MGMT.WAHA_SETTINGS.UPDATING')
        }}</span>
        <span v-else>{{ $t('INBOX_MGMT.WAHA_SETTINGS.UPDATE_BUTTON') }}</span>
      </button>
    </div>

    <div
      v-if="error"
      class="p-4 mb-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
    >
      <p class="text-red-800 dark:text-red-200">{{ error }}</p>
    </div>

    <div v-if="initialLoading" class="flex items-center justify-center py-8">
      <div class="text-center">
        <div
          class="w-12 h-12 border-4 border-slate-200 border-t-slate-600 rounded-full animate-spin mx-auto mb-3"
        />
        <span class="text-slate-600 dark:text-slate-400">{{
          $t('INBOX_MGMT.WAHA_SETTINGS.LOADING_SESSION')
        }}</span>
      </div>
    </div>

    <div v-else class="space-y-6">
      <!-- Status Card con transición suave -->
      <transition
        name="fade"
        mode="out-in"
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          :key="sessionStatus"
          class="p-5 bg-slate-50 dark:bg-slate-700 rounded-lg border border-slate-200 dark:border-slate-600"
        >
          <div class="flex items-center gap-4">
            <!-- Avatar: foto de perfil o indicador de estado -->
            <img
              v-if="sessionStatus === 'WORKING' && profileData?.picture"
              :src="profileData.picture"
              alt="WhatsApp"
              class="w-14 h-14 rounded-full object-cover ring-2 ring-green-400 dark:ring-green-600 shrink-0"
            />
            <div
              v-else
              class="w-12 h-12 rounded-full flex items-center justify-center shrink-0"
              :class="statusBgColor"
            >
              <div class="w-3 h-3 rounded-full" :class="statusDotColor" />
            </div>
            <div class="flex-1 min-w-0">
              <p
                class="text-xs font-medium text-slate-600 dark:text-slate-400 uppercase tracking-wide"
              >
                {{ $t('INBOX_MGMT.WAHA_SETTINGS.SESSION_STATUS') }}
              </p>
              <p class="text-xl font-bold mt-1 truncate" :class="statusColor">
                {{
                  sessionStatus === 'WORKING' && sessionMe?.pushName
                    ? sessionMe.pushName
                    : sessionStatusText
                }}
              </p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-1">
                {{ sessionSubtitle }}
              </p>
            </div>
            <span
              class="shrink-0 px-2.5 py-1 text-xs font-semibold rounded-full"
              :class="statusBgColor + ' ' + statusColor"
            >
              {{ sessionStatusText }}
            </span>
          </div>
        </div>
      </transition>

      <!-- Quota Alerts -->
      <div
        v-if="
          sessionStatus === 'WORKING' &&
          (timelockData?.locked || cappingData?.limited)
        "
        class="space-y-3"
      >
        <div
          v-if="timelockData?.locked"
          class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 flex items-center gap-3"
        >
          <svg
            class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <div>
            <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.TIMELOCK_TITLE') }}
            </p>
            <p class="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.TIMELOCK_DESC') }}
            </p>
          </div>
        </div>
        <div
          v-if="cappingData?.limited"
          class="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-200 dark:border-amber-800 flex items-center gap-3"
        >
          <svg
            class="w-5 h-5 text-amber-600 dark:text-amber-400 shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"
            />
          </svg>
          <div>
            <p class="text-sm font-semibold text-amber-900 dark:text-amber-100">
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.CAPPING_TITLE') }}
            </p>
            <p class="text-xs text-amber-700 dark:text-amber-300 mt-0.5">
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.CAPPING_DESC') }}
            </p>
          </div>
        </div>
      </div>

      <!-- WhatsApp Profile Editor -->
      <div
        v-if="sessionStatus === 'WORKING'"
        class="border border-slate-200 dark:border-slate-600 rounded-lg overflow-hidden"
      >
        <div
          class="px-5 py-3 bg-slate-100 dark:bg-slate-700 border-b border-slate-200 dark:border-slate-600 flex items-center gap-2"
        >
          <svg
            class="w-4 h-4 text-slate-500 dark:text-slate-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
            />
          </svg>
          <h4 class="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_TOGGLE') }}
          </h4>
        </div>
        <div class="p-5 bg-white dark:bg-slate-800">
          <div class="flex gap-5">
            <img
              v-if="profileData?.picture"
              :src="profileData.picture"
              alt="WhatsApp"
              class="w-16 h-16 rounded-full object-cover border border-slate-200 dark:border-slate-600 shrink-0"
            />
            <div
              v-else
              class="w-16 h-16 rounded-full bg-slate-100 dark:bg-slate-700 flex items-center justify-center shrink-0 border border-slate-200 dark:border-slate-600"
            >
              <svg
                class="w-8 h-8 text-slate-400 dark:text-slate-500"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                />
              </svg>
            </div>
            <div class="flex-1 space-y-4">
              <!-- Profile Name -->
              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1"
                >
                  {{ $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_NAME') }}
                </label>
                <div class="flex gap-2">
                  <input
                    v-model="profileNameEdit"
                    type="text"
                    class="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
                    :placeholder="
                      $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_NAME_PLACEHOLDER')
                    "
                  />
                  <button
                    type="button"
                    :disabled="savingProfile"
                    class="px-3 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 transition-colors"
                    @click="saveProfileName"
                  >
                    {{ $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_SAVE') }}
                  </button>
                </div>
              </div>
              <!-- Profile Status (About) -->
              <div>
                <label
                  class="block text-xs font-medium text-slate-600 dark:text-slate-300 mb-1"
                >
                  {{ $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_STATUS') }}
                </label>
                <div class="flex gap-2">
                  <input
                    v-model="profileStatusEdit"
                    type="text"
                    class="flex-1 px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 text-sm"
                    :placeholder="
                      $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_STATUS_PLACEHOLDER')
                    "
                  />
                  <button
                    type="button"
                    :disabled="savingProfile"
                    class="px-3 py-2 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 transition-colors"
                    @click="saveProfileStatus"
                  >
                    {{ $t('INBOX_MGMT.WAHA_SETTINGS.PROFILE_SAVE') }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- QR Code Section con transición -->
      <div
        v-if="sessionStatus === 'SCAN_QR_CODE'"
        class="overflow-hidden rounded-xl border border-slate-200 bg-white dark:border-slate-600 dark:bg-slate-800"
      >
        <div
          class="grid grid-cols-2 border-b border-slate-200 dark:border-slate-600"
        >
          <button
            type="button"
            class="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors"
            :class="
              authMethod === 'qr'
                ? 'border-b-2 border-woot-500 bg-woot-50 text-woot-600 dark:bg-woot-900/20 dark:text-woot-400'
                : 'text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/50'
            "
            @click="authMethod = 'qr'"
          >
            <span class="i-lucide-qr-code w-4 h-4" />
            {{ $t('INBOX_MGMT.WAHA_SETTINGS.AUTH_TAB_QR') }}
          </button>
          <button
            type="button"
            class="flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium transition-colors"
            :class="
              authMethod === 'code'
                ? 'border-b-2 border-woot-500 bg-woot-50 text-woot-600 dark:bg-woot-900/20 dark:text-woot-400'
                : 'text-slate-500 hover:bg-slate-50 dark:text-slate-400 dark:hover:bg-slate-700/50'
            "
            @click="authMethod = 'code'"
          >
            <span class="i-lucide-send w-4 h-4" />
            {{ $t('INBOX_MGMT.WAHA_SETTINGS.AUTH_TAB_CODE') }}
          </button>
        </div>
      </div>
      <transition
        name="slide-fade"
        enter-active-class="transition-all duration-300 ease-out"
        leave-active-class="transition-all duration-200"
        enter-from-class="opacity-0 -translate-y-2"
        leave-to-class="opacity-0 -translate-y-2"
      >
        <div
          v-if="sessionStatus === 'SCAN_QR_CODE' && authMethod === 'qr'"
          class="space-y-4"
        >
          <div
            class="p-6 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl"
          >
            <!-- Header con título y countdown -->
            <div class="flex items-center justify-between mb-4">
              <div class="flex items-center gap-2">
                <svg
                  class="w-5 h-5 text-blue-600 dark:text-blue-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
                  />
                </svg>
                <h4
                  class="text-base font-semibold text-slate-900 dark:text-slate-100"
                >
                  {{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_TITLE') }}
                </h4>
              </div>
              <!-- Countdown badge fuera de la imagen -->
              <div
                v-if="qrCountdown > 0"
                class="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 rounded-lg"
              >
                <svg
                  class="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
                {{
                  $t('INBOX_MGMT.WAHA_SETTINGS.SECONDS', {
                    seconds: qrCountdown,
                  })
                }}
              </div>
            </div>

            <!-- Pasos -->
            <ol
              class="text-sm text-slate-600 dark:text-slate-300 space-y-1.5 ml-4 list-decimal mb-5"
            >
              <li>{{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_1') }}</li>
              <li>{{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_2') }}</li>
              <li>{{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_3') }}</li>
              <li>{{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_4') }}</li>
            </ol>

            <!-- QR Code -->
            <div class="flex flex-col items-center gap-4">
              <transition
                name="fade"
                mode="out-in"
                enter-active-class="transition-opacity duration-300"
                leave-active-class="transition-opacity duration-300"
                enter-from-class="opacity-0"
                leave-to-class="opacity-0"
              >
                <div v-if="loadingQR" key="loading" class="text-center py-12">
                  <div
                    class="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-3"
                  />
                  <span class="text-slate-600 dark:text-slate-400">{{
                    $t('INBOX_MGMT.WAHA_SETTINGS.QR_LOADING')
                  }}</span>
                </div>
                <div v-else-if="qrCode" key="qr" class="relative">
                  <img
                    :src="`data:image/png;base64,${qrCode}`"
                    alt="QR Code"
                    class="w-full max-w-xs h-auto rounded-xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity bg-white p-3"
                    @click="showQRModal = true"
                  />
                </div>
              </transition>

              <!-- Botones de acción -->
              <div class="flex items-center gap-3">
                <button
                  class="px-4 py-2 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors flex items-center gap-2"
                  :disabled="loadingQR"
                  @click="fetchQRCode"
                >
                  <svg
                    class="w-4 h-4"
                    :class="{ 'animate-spin': loadingQR }"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  {{ $t('INBOX_MGMT.WAHA_SETTINGS.UPDATE_BUTTON') }}
                </button>
                <button
                  class="px-4 py-2 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors flex items-center gap-2"
                  @click="showQRModal = true"
                >
                  <svg
                    class="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7"
                    />
                  </svg>
                  {{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_FULL_SIZE') }}
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>

      <!-- Pairing Code alternative -->
      <div
        v-if="sessionStatus === 'SCAN_QR_CODE' && authMethod === 'code'"
        class="space-y-3"
      >
        <div
          class="p-5 bg-slate-50 dark:bg-slate-700/50 border border-slate-200 dark:border-slate-600 rounded-xl"
        >
          <div class="mb-5 flex items-start gap-3">
            <span class="i-lucide-smartphone w-5 h-5 mt-0.5 text-woot-500" />
            <div>
              <h4 class="font-semibold text-slate-900 dark:text-slate-100">
                {{ $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_TITLE') }}
              </h4>
              <p class="mt-1 text-sm text-slate-600 dark:text-slate-300">
                {{ $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_DESC') }}
              </p>
            </div>
          </div>
          <div class="flex flex-col gap-3 sm:flex-row">
            <input
              v-model="pairingPhoneNumber"
              type="tel"
              :placeholder="
                $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_PLACEHOLDER')
              "
              class="flex-1 px-4 py-2.5 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-woot-500 focus:border-transparent text-sm"
            />
            <button
              type="button"
              :disabled="loadingPairing || !pairingPhoneNumber"
              class="px-4 py-2.5 text-sm font-medium text-white bg-woot-500 rounded-lg hover:bg-woot-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
              @click="requestPairingCode"
            >
              {{
                loadingPairing
                  ? $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_REQUESTING')
                  : $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_REQUEST')
              }}
            </button>
          </div>

          <p
            v-if="pairingError"
            class="mt-3 text-sm text-red-600 dark:text-red-400"
          >
            {{ pairingError }}
          </p>

          <div
            v-if="pairingCode"
            class="mt-5 rounded-xl border border-slate-200 bg-white p-6 text-center shadow-sm dark:border-slate-600 dark:bg-slate-800"
          >
            <p
              class="mb-3 text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400"
            >
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_LABEL') }}
            </p>
            <p
              class="font-mono text-3xl font-semibold tracking-[0.3em] text-slate-900 dark:text-white sm:text-4xl"
            >
              {{ pairingCode }}
            </p>
            <p class="mt-4 text-xs text-slate-500 dark:text-slate-400">
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.PAIRING_CODE_INSTRUCTION') }}
            </p>
          </div>
        </div>
      </div>

      <!-- Action Buttons - estilo Chatwoot -->
      <div class="flex items-center justify-center gap-3 py-2">
        <button
          :disabled="actionLoading"
          class="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 dark:border-slate-600 hover:border-green-300 dark:hover:border-green-700 hover:bg-green-50 dark:hover:bg-green-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.START_SESSION_TITLE')"
          @click="startSession"
        >
          <span
            class="w-10 h-10 rounded-full flex items-center justify-center bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 group-hover:bg-green-600 group-hover:text-white transition-colors"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM9.555 7.168A1 1 0 008 8v4a1 1 0 001.555.832l3-2a1 1 0 000-1.664l-3-2z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <span
            class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >{{ $t('INBOX_MGMT.WAHA_SETTINGS.START_SESSION') }}</span
          >
        </button>

        <button
          :disabled="actionLoading"
          class="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 dark:border-slate-600 hover:border-red-300 dark:hover:border-red-700 hover:bg-red-50 dark:hover:bg-red-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.STOP_SESSION_TITLE')"
          @click="stopSession"
        >
          <span
            class="w-10 h-10 rounded-full flex items-center justify-center bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 group-hover:bg-red-600 group-hover:text-white transition-colors"
          >
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path
                fill-rule="evenodd"
                d="M10 18a8 8 0 100-16 8 8 0 000 16zM8 7a1 1 0 00-1 1v4a1 1 0 001 1h4a1 1 0 001-1V8a1 1 0 00-1-1H8z"
                clip-rule="evenodd"
              />
            </svg>
          </span>
          <span
            class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >{{ $t('INBOX_MGMT.WAHA_SETTINGS.STOP_SESSION') }}</span
          >
        </button>

        <button
          :disabled="actionLoading"
          class="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 dark:border-slate-600 hover:border-blue-300 dark:hover:border-blue-700 hover:bg-blue-50 dark:hover:bg-blue-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.RESTART_SESSION_TITLE')"
          @click="restartSession"
        >
          <span
            class="w-10 h-10 rounded-full flex items-center justify-center bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 group-hover:bg-blue-600 group-hover:text-white transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          </span>
          <span
            class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >{{ $t('INBOX_MGMT.WAHA_SETTINGS.RESTART_SESSION') }}</span
          >
        </button>

        <button
          :disabled="actionLoading"
          class="group flex flex-col items-center gap-1.5 p-3 rounded-xl border border-slate-200 dark:border-slate-600 hover:border-orange-300 dark:hover:border-orange-700 hover:bg-orange-50 dark:hover:bg-orange-900/20 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
          :title="$t('INBOX_MGMT.WAHA_SETTINGS.LOGOUT_SESSION_TITLE')"
          @click="logoutSession"
        >
          <span
            class="w-10 h-10 rounded-full flex items-center justify-center bg-orange-100 dark:bg-orange-900/30 text-orange-600 dark:text-orange-400 group-hover:bg-orange-600 group-hover:text-white transition-colors"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
              />
            </svg>
          </span>
          <span
            class="text-xs font-medium text-slate-600 dark:text-slate-400"
            >{{ $t('INBOX_MGMT.WAHA_SETTINGS.LOGOUT_SESSION') }}</span
          >
        </button>
      </div>

      <!-- Action feedback -->
      <transition
        name="fade"
        enter-active-class="transition-opacity duration-300"
        leave-active-class="transition-opacity duration-300"
        enter-from-class="opacity-0"
        leave-to-class="opacity-0"
      >
        <div
          v-if="actionLoading"
          class="p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg"
        >
          <div class="flex items-center gap-3">
            <div
              class="w-5 h-5 border-2 border-blue-200 border-t-blue-600 rounded-full animate-spin"
            />
            <span class="text-sm text-blue-800 dark:text-blue-200">{{
              $t('INBOX_MGMT.WAHA_SETTINGS.PROCESSING_ACTION')
            }}</span>
          </div>
        </div>
      </transition>
    </div>

    <div
      v-if="showQRModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
      @click="showQRModal = false"
      @keydown.esc="showQRModal = false"
    >
      <div
        class="relative max-w-3xl w-full p-6 bg-white dark:bg-slate-800 rounded-2xl shadow-2xl"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-semibold text-slate-900 dark:text-slate-100">
            {{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_TITLE') }}
          </h3>
          <div class="flex items-center gap-3">
            <span
              class="px-3 py-1 text-xs font-medium text-white bg-blue-600 rounded-full"
            >
              {{
                $t('INBOX_MGMT.WAHA_SETTINGS.SECONDS', { seconds: qrCountdown })
              }}
            </span>
            <button
              class="px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-100 dark:bg-blue-900/30 hover:bg-blue-200 dark:hover:bg-blue-900/50 rounded-lg transition-colors flex items-center gap-1.5"
              :disabled="loadingQR"
              @click="fetchQRCode"
            >
              <svg
                class="w-4 h-4"
                :class="{ 'animate-spin': loadingQR }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.UPDATE_BUTTON') }}
            </button>
            <button
              class="px-3 py-1.5 text-sm font-medium text-slate-700 dark:text-slate-300 bg-slate-100 dark:bg-slate-700 hover:bg-slate-200 dark:hover:bg-slate-600 rounded-lg transition-colors"
              @click="showQRModal = false"
            >
              {{ $t('INBOX_MGMT.WAHA_SETTINGS.CLOSE_MODAL') }}
            </button>
          </div>
        </div>
        <div class="flex justify-center">
          <img
            :src="`data:image/png;base64,${qrCode}`"
            alt="QR Code"
            class="max-w-lg w-full h-auto rounded-lg shadow-lg"
          />
        </div>
        <div
          class="mt-4 flex items-center justify-center gap-2 text-sm text-slate-500 dark:text-slate-400"
        >
          <svg
            class="w-4 h-4 text-blue-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <span>{{ $t('INBOX_MGMT.WAHA_SETTINGS.QR_STEP_3') }}</span>
        </div>
      </div>
    </div>
  </div>
</template>
